import {useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ContentCopy} from '@mui/icons-material';
import {Grid, Typography} from '@mui/material';

import {getEmailTemplateText} from './EmailTemplate.data';
import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import {RichTextEditor} from '../../../../../../../components/RichTextEditor/RichTextEditor';
import {SimpleFormikForm} from '../../../../../../../components/SimpleFormikForm/SimpleFormikForm';
import {FieldType} from '../../../../../../../components/SimpleFormikForm/types';
import {CompanyContext} from '../../../../../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../../../../contexts/SnackBarContext/SnackBarContext';
import {greenColors, orangeColors} from '../../../../../../../layouts/theme/designSystem';
import {SupportedLanguages} from '../../../../../../../types/SupportedLanguages';
import {copyTextToClipBoard} from '../../../../../../../utils/copyTextToClipboard/copyTextToClipBoard';
import {transformDate} from '../../../../../../../utils/dateUtils/dateFormattingUtils';
import {noopFunction} from '../../../../../../../utils/noopFunction';
import {findCurrentSurvey} from '../../../../../../../utils/surveyUtils/findCurrentSurvey';
import {formatSurveyLink} from '../../../../../../../utils/surveyUtils/formatSurveyLink';

interface FormValues {
  language: SupportedLanguages;
}

interface Props {
  onClose?: () => void;
}

export const EmailTemplate = ({onClose}: Props) => {
  const language = useContext(LanguageContext);
  const {t} = useTranslation();
  const {t: translate} = useTranslation('translation', {keyPrefix: 'companySurveys.emailTemplate'});
  const isDialogMode = Boolean(onClose);
  const {surveys} = useContext(CompanyContext);
  const [emailLanguage, setEmailLanguage] = useState<FormValues>({language});
  const {dispatchMessage} = useContext(SnackBarContext);
  const [emailText, setEmailText] = useState('');
  const [hoveredSection, setHoveredSection] = useState<'template' | 'link' | 'text' | null>(null);

  const onLanguageToggle = (value: string) => {
    setEmailLanguage({language: value as SupportedLanguages});
  };

  const handleHover = (section: 'template' | 'link' | 'text' | null) => {
    setHoveredSection(section);
  };

  const onCopyEmailClick = async () => {
    try {
      await copyTextToClipBoard(emailText);
      dispatchMessage(translate('actions.copyTemplate.success'), 'success');
    } catch (e: any) {
      dispatchMessage(translate('actions.copyTemplate.error'), 'error');
    }
  };

  const readText = (value: string) => {
    setEmailText(value);
  };

  const fields: FieldType<keyof FormValues>[] = [
    {
      componentType: 'radio',
      fieldName: 'language',
      direction: 'row',
      onChangeListener: onLanguageToggle,
      options: [
        {value: 'de', label: translate('labels.german')},
        {value: 'en', label: translate('labels.english')},
      ],
    },
  ];

  useEffect(() => {
    setEmailLanguage({language});
  }, [language]);

  if (surveys.isPending) {
    return null;
  }

  const currentSurvey = findCurrentSurvey(surveys?.data?.cultureSurveys || []);

  if (!currentSurvey) {
    return null;
  }

  const copyUrlLink = async () => {
    try {
      await copyTextToClipBoard(formatSurveyLink(currentSurvey));
      dispatchMessage(translate('actions.copyLink.success'), 'success');
    } catch (e: any) {
      dispatchMessage(translate('actions.copyLink.error'), 'error');
    }
  };

  const getClickableColor = (section: 'template' | 'link' | 'text' | null): string => {
    if (section === hoveredSection) {
      return greenColors[500];
    }
    return greenColors[700];
  };

  return (
    <Grid container className='transition-all'>
      <Grid container flexDirection='row' alignItems='center' justifyContent='space-between' paddingBottom='16px'>
        <Typography variant='h4'>{translate('templateTitle')}</Typography>
        <SimpleFormikForm onSubmit={noopFunction} hideSubmit={true} initialValues={emailLanguage} fields={fields} />
      </Grid>
      <Grid
        bgcolor={orangeColors[5]}
        borderRadius='16px'
        padding='19px 28px 19px 14px'
        border={`1px solid ${orangeColors[3]}`}
      >
        <RichTextEditor
          isEditable={false}
          hideToolbar
          readText={readText}
          defaultText={getEmailTemplateText(
            emailLanguage.language,
            formatSurveyLink(currentSurvey),
            transformDate(currentSurvey.endsAt, 'numeric')
          )}
        />
        {!isDialogMode && (
          <Grid container flexDirection='row-reverse' paddingTop='10px'>
            <EmpionButton
              onMouseEnter={() => handleHover('template')}
              onMouseLeave={() => handleHover(null)}
              sx={{backgroundColor: 'transparent', width: '35px', height: '35px'}}
              hoverBackgroundColor='transparent'
              aria-label='copy-email-template'
              onClick={onCopyEmailClick}
            >
              <ContentCopy sx={{color: getClickableColor('template')}} />{' '}
            </EmpionButton>
          </Grid>
        )}
      </Grid>
      <Typography variant='h4' paddingTop='18px' paddingBottom='16px'>
        {translate('surveyLink')}
      </Typography>
      <Grid
        container
        padding='19px 28px 19px 14px'
        justifyContent='space-between'
        alignItems='center'
        flexWrap='nowrap'
        bgcolor={orangeColors[5]}
        borderRadius='16px'
        marginBottom='32px'
        border={`1px solid ${orangeColors[3]}`}
      >
        <Typography
          onMouseEnter={() => handleHover('link')}
          onMouseLeave={() => handleHover(null)}
          onClick={copyUrlLink}
          fontSize='16px'
          fontWeight={700}
          sx={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            color: getClickableColor('link'),
            cursor: 'pointer',
          }}
        >
          {formatSurveyLink(currentSurvey)}
        </Typography>
        {!isDialogMode && (
          <EmpionButton
            onMouseEnter={() => handleHover('text')}
            onMouseLeave={() => handleHover(null)}
            sx={{backgroundColor: 'transparent', width: '35px', height: '35px'}}
            hoverBackgroundColor='transparent'
            aria-label='copy-survey-url'
            onClick={copyUrlLink}
          >
            <ContentCopy sx={{color: getClickableColor('text')}} />
          </EmpionButton>
        )}
      </Grid>
      {isDialogMode && (
        <Grid container gap={1.5} flexWrap='wrap' justifyContent='flex-end' marginTop={2}>
          <EmpionButton variant='primary' onClick={onCopyEmailClick}>
            {t('companySurveys.actionButtons.copyEmailTemplate')}
          </EmpionButton>
          <EmpionButton variant='primary' onClick={copyUrlLink}>
            {t('companySurveys.actionButtons.copySurveyLink')}
          </EmpionButton>
          <EmpionButton variant='primary' onClick={onClose}>
            {t('shared.cancel')}
          </EmpionButton>
        </Grid>
      )}
    </Grid>
  );
};
